import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { ArrowLeft, UsersRound } from "lucide-react";
import Header from "./ui/Header";
import Footer from "./ui/Footer";
import { jiraNotificationService } from "../services/JiraNotificationService";
import Carousel from './ui/Carousel';

const SignUp = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
  const [formData, setFormData] = useState({
    corporateEmail: '',
    companyName: '',
    firstName: '',
    lastName: '',
    primaryUseCase: '',
    specificUseCase: '',
    passCode: '',
    reasonForUse: ''
  });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [recaptchaChecked, setRecaptchaChecked] = useState(false);
  const [errors, setErrors] = useState({});

  // Load privacy policy URL from config.json
  useEffect(() => {
    fetch('/config.json', { cache: 'no-store' })
      .then(response => response.json())
      .then(config => {
        if (config.privacyPolicyUrl) {
          setPrivacyPolicyUrl(config.privacyPolicyUrl);
        }
      })
      .catch(error => console.error('Error loading privacy policy URL from config:', error));
  }, []);

  const navigateToNotification = (type, title, message) => {
    // Navigate to notification page with data
    navigate('/notification', {
      state: {
        type,
        title,
        message
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    console.log('🔍 Starting form validation...');
    
    if (!formData.corporateEmail) {
      newErrors.corporateEmail = 'Corporate email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.corporateEmail)) {
      newErrors.corporateEmail = 'Please enter a valid email address';
    }
    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.primaryUseCase) {
      newErrors.primaryUseCase = 'Please select your primary use case';
    }
    if (!formData.specificUseCase) {
      newErrors.specificUseCase = 'Please describe your specific use case';
    } else if (formData.specificUseCase.length < 10) {
      newErrors.specificUseCase = `Please provide at least 10 characters (currently ${formData.specificUseCase.length})`;
    }
    if (!agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the Terms & Conditions and Privacy Policy';
    }
    if (!recaptchaChecked) {
      newErrors.recaptchaChecked = 'Please confirm you are not a robot';
    }
    
    console.log('📊 Validation errors found:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 Form submission started');
    console.log('📝 Form data:', formData);
    console.log('✅ Agree to terms:', agreeTerms);
    console.log('🤖 reCAPTCHA checked:', recaptchaChecked);
    
    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }
    
    console.log('✅ Form validation passed, proceeding with submission');
    setIsLoading(true);
    try {
      const userDetails = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.corporateEmail,
        companyName: formData.companyName,
        primaryUseCase: formData.primaryUseCase,
        reasonForUse: formData.specificUseCase, // Use specificUseCase instead of reasonForUse
        submittedAt: new Date().toLocaleString()
      };
      const notificationResult = await jiraNotificationService.sendAccountCreationNotification(userDetails);
      if (notificationResult.success) {
        navigateToNotification(
          'success',
          'Account Creation Request Submitted Successfully!',
          `Dear ${formData.firstName} ${formData.lastName},\n\nYour request for account creation has been received and a JIRA support ticket has been created for our support team.\n\n✅ Ticket: ${notificationResult.message}\n\nOur team will review your request and contact you at ${formData.corporateEmail} within 24-48 hours.\n\nThank you for your interest in Adapt Learning Platform!`
        );
      } else {
        navigateToNotification(
          'warning',
          'Account Creation Request Submitted!',
          `Dear ${formData.firstName} ${formData.lastName},\n\nYour request has been received. However, there was an issue with our notification system:\n\n❌ ${notificationResult.error}\n\n${notificationResult.suggestion || 'Please contact support if this issue persists.'}\n\nOur team will still review your request and contact you at ${formData.corporateEmail}.`
        );
      }
    } catch (error) {
      navigateToNotification(
        'error',
        'Submission Error',
        `An error occurred while submitting your request. Please try again later.\n\nError: ${error.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/');
  };

  const handleLoginRedirect = () => {
    navigate('/');
  };
  return (
    <section className="h-screen bg-white flex flex-col">
      <Header />
      <div className="flex flex-1 overflow-hidden bg-white">
        <div className="flex w-full h-full">
          {/* Left Panel - Carousel */}
          <Carousel />
          
          {/* Right Panel - SignUp Form */}
          <div className="w-full lg:w-1/2 flex justify-center p-8 bg-white overflow-y-auto max-h-full">
            <div className="w-full max-w-md py-4">
              {/* Back Button */}
              <div className="mb-6 text-left">
                <Button
                  variant="ghost"
                  onClick={handleGoBack}
                  className="text-gray-500 hover:text-gray-700 p-0 font-normal flex"
                >
                  <ArrowLeft className="w-4 h-4 mr-2 inline" />
                  Back to Sign in
                </Button>
              </div>
              
              {/* Header Section */}
              <div className="flex flex-col items-center mb-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mb-4">
                  <UsersRound />
                </div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">Create Your Account</h1>
                <p className="text-gray-500 text-center text-sm leading-relaxed">
                  Join the Adapt Authoring Tool community and start<br />
                  creating amazing learning experiences
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 text-left">
                {/* First Name & Last Name */}
                <div className="grid gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name <span className="text-error-500">*</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter your first name"
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none transition-colors ${errors.firstName ? 'border-error-500' : 'border-gray-300'}`}
                    />
                    {errors.firstName && <p className="text-error-500 text-sm mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name <span className="text-error-500">*</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter your last name"
                      className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none transition-colors ${errors.lastName ? 'border-error-500' : 'border-gray-300'}`}
                    />
                    {errors.lastName && <p className="text-error-500 text-sm mt-1">{errors.lastName}</p>}
                  </div>
                </div>
                
                {/* Company Name */}
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    id="companyName"
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Enter your company name"
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none transition-colors ${errors.companyName ? 'border-error-500' : 'border-gray-300'}`}
                  />
                  {errors.companyName && <p className="text-error-500 text-sm mt-1">{errors.companyName}</p>}
                </div>
                
                {/* Email Address */}
                <div>
                  <label htmlFor="corporateEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-error-500">*</span>
                  </label>
                  <input
                    id="corporateEmail"
                    type="email"
                    name="corporateEmail"
                    value={formData.corporateEmail}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none transition-colors ${errors.corporateEmail ? 'border-error-500' : 'border-gray-300'}`}
                  />
                  {errors.corporateEmail && <p className="text-error-500 text-sm mt-1">{errors.corporateEmail}</p>}
                </div>
                
                {/* Reason for Using Adapt Authoring Tool */}
                <div>
                  <label htmlFor="primaryUseCase" className="block text-sm font-medium text-gray-700 mb-2">
                    Purpose of Using Adapt Authoring Tool <span className="text-error-500">*</span>
                  </label>
                  <select
                    id="primaryUseCase"
                    name="primaryUseCase"
                    value={formData.primaryUseCase}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none transition-colors bg-white ${errors.primaryUseCase ? 'border-error-500' : 'border-gray-300'} ${!formData.primaryUseCase ? 'text-gray-400' : 'text-gray-900'}`}
                  >
                    <option value="" disabled hidden className="text-gray-400">Select your primary use case</option>
                    <option value="Course Creation" className="text-gray-900">Course Creation</option>
                    <option value="Other" className="text-gray-900">Other</option>
                  </select>
                  {errors.primaryUseCase && <p className="text-error-500 text-sm mt-1">{errors.primaryUseCase}</p>}
                </div>
                
                {/* Specific Use Case */}
                <div>
                  <label htmlFor="specificUseCase" className="block text-sm font-medium text-gray-700 mb-2">
                    Please describe your specific use case <span className="text-error-500">*</span>
                  </label>
                  <textarea
                    id="specificUseCase"
                    name="specificUseCase"
                    value={formData.specificUseCase}
                    onChange={handleInputChange}
                    placeholder="Please describe your specific use case in detail (minimum 10 characters). For example: 'I need to create interactive e-learning modules for medical training courses at our hospital.'"
                    className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none transition-colors resize-none ${errors.specificUseCase ? 'border-error-500' : 'border-gray-300'}`}
                    rows="4"
                    maxLength={3000}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <div>
                      {errors.specificUseCase && <p className="text-error-500 text-sm">{errors.specificUseCase}</p>}
                    </div>
                    <p className={`text-xs ${formData.specificUseCase.length < 10 ? 'text-error-500' : 'text-gray-500'}`}>
                      {formData.specificUseCase.length}/3000 characters {formData.specificUseCase.length < 10 && '(minimum 10 required)'}
                    </p>
                  </div>
                </div>
                
                {/* Terms & Conditions Checkbox */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      checked={agreeTerms}
                      onChange={e => setAgreeTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 text-brand-blue focus:ring-primary-400 border-gray-300 rounded flex-shrink-0"
                    />
                    <label htmlFor="agreeTerms" className="text-sm text-gray-700 leading-5">
                      I agree to the{' '}
                      <a
                        href={privacyPolicyUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-blue hover:text-primary-600 underline"
                        aria-label="Open Laerdal's Privacy Policy in a new tab"
                      >
                        Laerdal's Privacy Policy
                      </a>
                      {'. '}<span className="text-error-500">*</span>
                    </label>
                  </div>
                  {errors.agreeTerms && <p className="text-error-500 text-sm mt-1">{errors.agreeTerms}</p>}
                </div>
                
                {/* reCAPTCHA Checkbox */}
                <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="recaptchaChecked"
                      checked={recaptchaChecked}
                      onChange={e => setRecaptchaChecked(e.target.checked)}
                      className="h-4 w-4 text-brand-blue focus:ring-primary-400 border-gray-300 rounded"
                    />
                    <label htmlFor="recaptchaChecked" className="text-sm text-gray-700">
                      I am not a robot
                    </label>
                    <div className="ml-auto text-xs text-gray-500">reCAPTCHA</div>
                  </div>
                  {errors.recaptchaChecked && <p className="text-error-500 text-sm mt-1">{errors.recaptchaChecked}</p>}
                </div>
                
                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full text-white py-4 text-base font-medium rounded-md transition-colors bg-brand-blue hover:bg-brand-darkBlue focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
                  style={{
                    backgroundColor: isLoading ? '#6B7280' : '#2E7FA1',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
                
                {/* Already have account */}
                <p className="text-center text-sm text-gray-600 mt-6">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="text-brand-blue hover:text-primary-600 underline font-medium"
                    onClick={handleLoginRedirect}
                  >
                    Sign In
                  </button>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </section>
  );

}
export default SignUp;
